import type { DateRange } from "react-day-picker";

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const STATISTICS_PRESET_DAYS = [7, 28, 90] as const;

export type StatisticsPresetDays = (typeof STATISTICS_PRESET_DAYS)[number];

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Rolling window ending at `now` (same idea as previous statistics range util). */
export function getRollingBounds(
  days: number,
  now = new Date(),
): { start: Date; end: Date } {
  const end = new Date(now.getTime());
  const start = new Date(end.getTime() - days * MS_PER_DAY);
  return { start, end };
}

/** Map rolling bounds to calendar days for DayPicker highlighting. */
export function rollingBoundsToDateRange(bounds: {
  start: Date;
  end: Date;
}): DateRange {
  return {
    from: startOfDay(bounds.start),
    to: startOfDay(bounds.end),
  };
}

export function presetToDateRange(
  days: StatisticsPresetDays,
  now = new Date(),
): DateRange {
  return rollingBoundsToDateRange(getRollingBounds(days, now));
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const shortFmt = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export function formatStatisticsFilterLabel(
  preset: StatisticsPresetDays | null,
  range: DateRange | undefined,
): string {
  if (preset !== null) {
    return `Last ${preset} days`;
  }
  if (!range?.from) {
    return "Select a date range";
  }
  if (range.to === undefined) {
    return `${shortFmt.format(range.from)} – …`;
  }
  if (isSameCalendarDay(range.from, range.to)) {
    return shortFmt.format(range.from);
  }
  return `${shortFmt.format(range.from)} – ${shortFmt.format(range.to)}`;
}

export function dateRangeToAnalyticsBounds(
  range: DateRange | undefined,
): { start: Date; end: Date } | null {
  if (!range?.from) return null;
  const to = range.to ?? range.from;
  return {
    start: startOfDay(range.from),
    end: endOfDay(to),
  };
}
